import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { Tooltip } from "@tql/ui-components-react/kendo/tooltip";
import { helpCircleLine } from "@tql/ui-components-react/untitled/icons";

import { getCarrierLogoUrl } from "~/features/quote/utils/carrier-selection-utils.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import { getBaseUrl } from "~/shared/utils/get-base-url.ts";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import { getCarrierServiceLevelDisplayName } from "../../utils/get-carrier-service-level-display-name.ts";
import type { CarrierCardColumnConfig } from "../shared/carrier-card-layout.tsx";
import { CarrierCardLayout } from "../shared/carrier-card-layout.tsx";

export const RateConfirmationCarrier = ({
	carrier,
	isCarrierSelected,
	onCarrierSelection,
}: {
	carrier: CarrierPrice;
	isCarrierSelected: boolean;
	onCarrierSelection: (carrier: CarrierPrice) => void;
}) => {
	const baseUrl = getBaseUrl();

	const selectionControl = (
		<div style={{ alignSelf: "center", pointerEvents: "none" }}>
			<Checkbox
				aria-label={`Select ${
					carrier.carrier.displayName ?? carrier.carrier.name
				}`}
				checked={isCarrierSelected}
				tabIndex={-1}
			/>
		</div>
	);

	const logo = (
		<div>
			<img
				alt={`Carrier logo for ${
					carrier.carrier.displayName ?? carrier.carrier.name
				}`}
				className={"carrier-card-image"}
				src={getCarrierLogoUrl(carrier.carrier.scac, baseUrl)}
			/>
		</div>
	);

	const nameSection = (
		<div className={"carrier-card-col carrier-name-title-details"}>
			<div>{carrier.carrier.displayName || carrier.carrier.name}</div>
			<div>
				<a
					className={"tql-link"}
					href={`${baseUrl}/documents/carrier-restrictions/ProhibitedCommodities_${carrier.carrier.scac}.pdf`}
					onClick={(e) => e.stopPropagation()}
					rel={"noopener noreferrer"}
					target={"_blank"}
				>
					View Restrictions
				</a>
			</div>
			{carrier.carrier.isPreferred ? (
				<img
					alt={"Preferred Carrier"}
					className={"preferred-carrier"}
					src={`${baseUrl}/images/preferredCarrier.png`}
				/>
			) : null}
			{carrier.carrier.isCarrierOfTheYear ? (
				<img
					alt={"Carrier of the Year"}
					className={"preferred-carrier"}
					src={`${baseUrl}/images/carrierOfTheYear.png`}
				/>
			) : null}
		</div>
	);

	const columns: CarrierCardColumnConfig[] = [
		{
			className: "customer-rate-col",
			content: (
				<span style={{ fontWeight: 700, fontSize: "20px" }}>
					{formatCurrency(carrier.customerRate)}
				</span>
			),
			id: "customer-rate",
			title: "Customer Rate",
		},
		{
			className: "service-level-col",
			content: (
				<div style={{ paddingTop: "0.25rem" }}>
					{getCarrierServiceLevelDisplayName(carrier)}
				</div>
			),
			id: "service-level",
			title: "Service Level",
		},
		{
			className: "service-days-col",
			content: (
				<div style={{ paddingTop: "0.25rem" }}>
					{carrier.transitDays} {carrier.transitDays === 1 ? "Day" : "Days"}{" "}
					Estimated
				</div>
			),
			id: "service-days",
			title: "Service Days",
		},
		{
			className: "service-type-col",
			content: (
				<div style={{ paddingTop: "0.25rem" }}>{carrier.serviceType}</div>
			),
			id: "service-type",
			title: (
				<>
					<div>Service Type&nbsp;</div>
					<Tooltip>
						<div title={"this is the tooltip"}>
							<SvgIcon icon={helpCircleLine} />
						</div>
					</Tooltip>
				</>
			),
		},
		{
			className: "max-liability-col",
			content: (
				<div style={{ paddingTop: "0.25rem" }}>
					<div className={"max-liability-detail tql-link"}>Varies By Class</div>
					<div className={"max-liability-detail"}>
						Max Liability
						{carrier.maxLiabilityOverride && carrier.maxLiabilityOverride > 0
							? ""
							: " (New / Used)"}
					</div>
					<div className={"max-liability-value"}>
						{carrier.maxLiabilityOverride && carrier.maxLiabilityOverride > 0
							? formatCurrency(carrier.maxLiabilityOverride)
							: `${formatCurrency(
									carrier.maxLiabilityNew,
								)} / ${formatCurrency(carrier.maxLiabilityUsed)}`}
					</div>
				</div>
			),
			id: "max-liability",
			title: (
				<>
					<div>Liability Per Pound&nbsp;</div>
					<Tooltip>
						<div title={"this is the tooltip"}>
							<SvgIcon icon={helpCircleLine} />
						</div>
					</Tooltip>
				</>
			),
		},
	];

	return (
		<CarrierCardLayout
			key={carrier.id}
			cardProps={{
				onClick: () => onCarrierSelection(carrier),
				onKeyDown: (e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						onCarrierSelection(carrier);
					}
				},
				role: "checkbox",
				"aria-checked": isCarrierSelected,
				style: { width: "100%", cursor: "pointer" },
				tabIndex: 0,
			}}
			columns={columns}
			contentProps={{
				className: "carrier-content",
			}}
			leftControl={selectionControl}
			logo={logo}
			nameSection={nameSection}
		/>
	);
};
